<template>
  <v-card :title="t('user_reports')">
    <v-form @submit.prevent="submit">
      <v-card-text class="pt-0 pb-0">
        <v-row>
          <v-col cols="4">
            <v-row>
              <v-col>
                <v-text-field
                  v-model="form.email.value.value"
                  :error-messages="form.email.errorMessage.value"
                  :label="t('email')"
                  density="compact"
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-checkbox
                  v-model="form.email_report.value.value"
                  :label="t('enable_email_report')"
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="4" class="border-s-md">
            <v-select
              :label="t('yearly_report_image')"
              clearable
              :items="parkImages"
              v-model="form.yearly_report_image.value.value"
              :error-messages="form.yearly_report_image.errorMessage.value"
              item-title="visual_name"
              item-value="id"
              hide-details
              density="compact"
            />
          </v-col>
          <v-col cols="4">
            <v-row no-gutters>
              <v-col>
                {{ t('preview') }}
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <div class="d-flex border-md">
                  <template v-if="form.yearly_report_image.value.value !== null">
                    <v-img
                      :width="300"
                      :src="yearlyUrl"
                    />
                  </template>
                </div>
              </v-col>
            </v-row>
            <v-row no-gutters class="mt-2">
              <v-col>
                <v-dialog max-width="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-btn
                      style="width: 100%"
                      v-bind="activatorProps"
                    >
                      {{ t('send_yearly_report_now') }}
                    </v-btn>
                  </template>
                  <template v-slot:default="{ isActive }">
                    <v-card :title="t('select_start_date')">
                      <v-card-text>
                        <v-row class="justify-center">
                          <v-date-picker
                            v-model="yrstart"
                            color="blue"
                            :allowed-dates="(date) => {return date < new Date()}"
                          ></v-date-picker>
                        </v-row>
                        <v-row>
                          <v-text-field :label="t('email')" v-model="yremail"></v-text-field>
                          <!-- TODO: Add validation and form! -->
                        </v-row>
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>

                        <v-btn
                          :text="t('send')"
                          color="green"
                          :loading="yrloading"
                          @click="yearlyReport();isActive.value = false"
                        ></v-btn>
                      </v-card-actions>
                    </v-card>
                  </template>
                </v-dialog>

              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn border type="submit">{{ t('save') }}</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup>
import { toRefs, computed, ref, onMounted } from "vue";
import { useModifyParkBase } from "./base.js";
import * as yup from 'yup';
import { sendYearlyReport } from "../../remote/park.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
// General State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark', 'parkImages']);
const { originalPark, parkImages } = toRefs(props);
const emit = defineEmits(['updated']);
const yrloading = ref(false);
const yrstart = ref(new Date());
const yremail = ref("");

const yearlyUrl = computed(() => {
  return parkImages.value.find(el => el['id'] === form.yearly_report_image.value.value)?.url || null;
});

function initYrstart() {
  const today = new Date();
  today.setDate(1);
  today.setFullYear(today.getFullYear() - 1);
  yrstart.value = today;
}

onMounted(() => {
  initYrstart();
});

// Form
// noinspection JSCheckFunctionSignatures
const schema = yup.object({
  email: yup.string().email(),
  email_report: yup.boolean(),
  yearly_report_image: yup.number().integer().nullable(),
});

const { form, submit } = useModifyParkBase(
  useAlerts, loadingOverlay, originalPark, schema, emit
);

async function yearlyReport() {
  yrloading.value = true;
  submit();
  let formattedDate = yrstart.value.toISOString().split('T')[0]; // YYYY-MM-DD
  await sendYearlyReport(useAlerts, originalPark.value.id, formattedDate, yremail.value);
  yrloading.value = false;
}
</script>

<i18n>
{
  "en": {
    "user_reports": "User Reports",
    "email": "Email",
    "enable_email_report": "Enable Email Report",
    "yearly_report_image": "Yearly Report Image",
    "preview": "Preview",
    "send_yearly_report_now": "Send Yearly Report Now",
    "select_start_date": "Select Start Date for Yearly Report",
    "send": "Send",
    "save": "Save"
  },
  "it": {
    "user_reports": "Rapporti Utente",
    "email": "Email",
    "enable_email_report": "Abilita Rapporto Email",
    "yearly_report_image": "Immagine Rapporto Annuale",
    "preview": "Anteprima",
    "send_yearly_report_now": "Invia Rapporto Annuale Ora",
    "select_start_date": "Seleziona Data di Inizio per il Rapporto Annuale",
    "send": "Invia",
    "save": "Salva"
  },
  "fr": {
    "user_reports": "Rapports Utilisateur",
    "email": "Email",
    "enable_email_report": "Activer le Rapport par Email",
    "yearly_report_image": "Image du Rapport Annuel",
    "preview": "Aperçu",
    "send_yearly_report_now": "Envoyer le Rapport Annuel Maintenant",
    "select_start_date": "Sélectionner la Date de Début pour le Rapport Annuel",
    "send": "Envoyer",
    "save": "Enregistrer"
  }
}
</i18n>

<style scoped>

</style>
